import { zodResolver } from "@hookform/resolvers/zod";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import * as React from "react";
import {
  Controller,
  FormProvider,
  Resolver,
  SubmitHandler,
  useForm,
  useFormContext,
} from "react-hook-form";

import {
  comparatorForSearchField,
  SearchField,
} from "@/shared/components/filters";
import { TriggerForEachPrimitive } from "@/shared/domain/actions/TriggerRecord";
import { QueryTarget } from "@/shared/domain/query";
import { Comparator } from "@/types";

import { ResourcePrefixSelector } from "./ResourcePrefixSelector";
import {
  TriggerCondition,
  triggerConditionInputSchema,
  TriggerConditionInputForm,
} from "./schema";
import styles from "./TriggerConditionInput.module.css";
import { buildFieldName, getDefaultPrefix, parseFieldName } from "./utils";

interface TriggerConditionInputProps {
  searchFields: SearchField[];
  conditionToEdit: TriggerCondition | null;
  onCreate: (condition: TriggerCondition) => void;
  onUpdate: (condition: TriggerCondition) => void;
  onCancel: () => void;
  forEachPrimitive: TriggerForEachPrimitive;
  availableMetadataKeys?: string[];
}

const emptyComparators: Comparator[] = [
  "EXISTS",
  "NOT_EXISTS",
  "IS_NULL",
  "IS_NOT_NULL",
];

/**
 * Internal component that uses form context.
 * This is separated to allow useFormContext to work properly.
 */
const TriggerConditionInputFields: React.FC<{
  searchFields: SearchField[];
  forEachPrimitive: TriggerForEachPrimitive;
  availableMetadataKeys: string[];
  onCancel: () => void;
  conditionToEdit: TriggerCondition | null;
}> = ({
  searchFields,
  forEachPrimitive,
  availableMetadataKeys,
  onCancel,
  conditionToEdit,
}) => {
  const form = useFormContext<TriggerConditionInputForm>();
  const currentTimezone = dayjs.tz.guess();

  // Watch form values for conditional rendering
  const selectedFieldName = form.watch("selectedField");
  const comparator = form.watch("comparator");
  const metadataType = form.watch("metadataType");

  // Find the selected field object
  const selectedField = React.useMemo(() => {
    return (
      searchFields.find((f) => f.name === selectedFieldName) || searchFields[0]
    );
  }, [searchFields, selectedFieldName]);

  // Get available comparators for current field
  const availableComparators = React.useMemo(() => {
    return comparatorForSearchField(
      selectedField,
      QueryTarget.Datasets,
      metadataType,
    );
  }, [selectedField, metadataType]);

  // Update comparator when field changes
  React.useEffect(() => {
    if (
      availableComparators.length > 0 &&
      !availableComparators.includes(comparator as Comparator)
    ) {
      form.setValue("comparator", availableComparators[0]);
    }
  }, [availableComparators, comparator, form]);

  const handleFieldChange = (fieldName: string) => {
    form.setValue("selectedField", fieldName);
    // Reset field-specific state
    const todayAt8AMLocal = dayjs()
      .tz(currentTimezone)
      .startOf("day")
      .add(8, "hour");
    form.setValue("value", "");
    form.setValue("metadataKey", "");
    form.setValue("metadataValue", "");
    form.setValue("dateValue", todayAt8AMLocal.toDate());
    // Note: Comparator validation is handled by the useEffect that watches
    // availableComparators. When the field changes, availableComparators will
    // update, and the useEffect will ensure the comparator remains valid.
  };

  const isEmptyComparator = emptyComparators.includes(comparator as Comparator);

  return (
    <Box className={styles.container}>
      {/* Resource Prefix Selector */}
      <Controller
        name="resourcePrefix"
        control={form.control}
        render={({ field }) => (
          <ResourcePrefixSelector
            value={field.value}
            onChange={field.onChange}
            forEachPrimitive={forEachPrimitive}
          />
        )}
      />

      {/* Field Selector */}
      <Controller
        name="selectedField"
        control={form.control}
        render={({ field }) => (
          <FormControl fullWidth size="small">
            <InputLabel id="field-label">Field</InputLabel>
            <Select
              labelId="field-label"
              value={field.value}
              label="Field"
              onChange={(e) => {
                handleFieldChange(e.target.value);
              }}
              data-cy="field-select"
            >
              {searchFields.map((f) => (
                <MenuItem key={f.name} value={f.name}>
                  {f.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      />

      {/* Comparator Selector */}
      <Controller
        name="comparator"
        control={form.control}
        render={({ field }) => (
          <FormControl fullWidth size="small">
            <InputLabel id="comparator-label">Comparator</InputLabel>
            <Select
              labelId="comparator-label"
              value={field.value}
              label="Comparator"
              onChange={field.onChange}
              data-cy="comparator-select"
            >
              {availableComparators.map((comp: Comparator) => (
                <MenuItem key={comp} value={comp}>
                  {comp}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      />

      {/* Value Input - varies by field type */}
      {!isEmptyComparator && (
        <>
          {selectedField.type === "date" && (
            <Controller
              name="dateValue"
              control={form.control}
              render={({ field }) => (
                <DatePicker
                  label={currentTimezone}
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(newValue) => {
                    field.onChange(newValue?.toDate() || null);
                  }}
                  timezone={currentTimezone}
                  slotProps={{ textField: { size: "small", fullWidth: true } }}
                  data-cy="date-picker"
                />
              )}
            />
          )}

          {selectedField.type === "metadata" && (
            <>
              <Controller
                name="metadataKey"
                control={form.control}
                render={({ field }) => (
                  <Autocomplete
                    freeSolo
                    options={availableMetadataKeys}
                    value={field.value}
                    onInputChange={(_, newValue) => field.onChange(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Metadata Key"
                        size="small"
                        data-cy="metadata-key-input"
                      />
                    )}
                  />
                )}
              />
              <Controller
                name="metadataValue"
                control={form.control}
                render={({ field }) => (
                  <TextField
                    label="Metadata Value"
                    value={field.value}
                    onChange={field.onChange}
                    size="small"
                    fullWidth
                    data-cy="metadata-value-input"
                  />
                )}
              />
              <Controller
                name="metadataType"
                control={form.control}
                render={({ field }) => (
                  <FormControl fullWidth size="small">
                    <InputLabel id="metadata-type-label">Value Type</InputLabel>
                    <Select
                      labelId="metadata-type-label"
                      value={field.value}
                      label="Value Type"
                      onChange={field.onChange}
                      data-cy="metadata-type-select"
                    >
                      <MenuItem value="string">String</MenuItem>
                      <MenuItem value="number">Number</MenuItem>
                      <MenuItem value="boolean">Boolean</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </>
          )}

          {selectedField.type !== "date" &&
            selectedField.type !== "metadata" && (
              <Controller
                name="value"
                control={form.control}
                render={({ field }) => (
                  <TextField
                    label="Value"
                    value={field.value}
                    onChange={field.onChange}
                    size="small"
                    fullWidth
                    data-cy="value-input"
                  />
                )}
              />
            )}
        </>
      )}

      {/* Action Buttons */}
      <Box className={styles.actions}>
        <Button variant="outlined" onClick={onCancel} data-cy="cancel-button">
          Cancel
        </Button>
        <Button variant="contained" type="submit" data-cy="submit-button">
          {conditionToEdit ? "Update Condition" : "Add Condition"}
        </Button>
      </Box>
    </Box>
  );
};

/**
 * Form component for creating or editing a single trigger condition.
 * This is the main export that wraps TriggerConditionInputFields with FormProvider.
 *
 * Features:
 * - Resource prefix selector (dataset or file)
 * - Field selector (tags, metadata, created, created_by)
 * - Comparator selector (varies by field type)
 * - Value input (varies by field type: text, date picker, metadata key/value)
 */
export const TriggerConditionInput: React.FC<TriggerConditionInputProps> = ({
  searchFields,
  conditionToEdit,
  onCreate,
  onUpdate,
  onCancel,
  forEachPrimitive,
  availableMetadataKeys = [],
}) => {
  const currentTimezone = dayjs.tz.guess();
  const todayAt8AMLocal = dayjs()
    .tz(currentTimezone)
    .startOf("day")
    .add(8, "hour");

  // Initialize form with default values or values from conditionToEdit
  const getDefaultValues = (): TriggerConditionInputForm => {
    if (conditionToEdit) {
      const { prefix, baseName } = parseFieldName(conditionToEdit.field);
      const fieldNameToMatch = baseName.startsWith("metadata.")
        ? "metadata"
        : baseName;

      let metadataKey = "";
      let metadataValue = "";
      let dateValue: Date | null = null;
      let value = "";

      if (baseName.startsWith("metadata.")) {
        const metadataMatch = conditionToEdit.field.match(/metadata\.(.+)$/);
        if (metadataMatch) {
          metadataKey = metadataMatch[1];
          metadataValue = String(conditionToEdit.value);
        }
      } else if (conditionToEdit.searchFieldType === "date") {
        dateValue = dayjs(conditionToEdit.value).toDate();
      } else {
        value = conditionToEdit.value;
      }

      // Ensure the comparator is valid for the selected field. If not, fall back
      // to the first available comparator for that field to avoid MUI Select warnings
      // about out-of-range values.
      const selectedFieldObj =
        searchFields.find((f) => f.name === fieldNameToMatch) ||
        searchFields[0];
      const availableComparatorsForField = comparatorForSearchField(
        selectedFieldObj,
        QueryTarget.Datasets,
        "string",
      );

      const comparatorToUse = availableComparatorsForField.includes(
        conditionToEdit.comparator as Comparator,
      )
        ? (conditionToEdit.comparator as Comparator)
        : availableComparatorsForField[0] || ("" as Comparator);

      return {
        resourcePrefix: prefix,
        selectedField: fieldNameToMatch,
        comparator: comparatorToUse,
        value,
        dateValue,
        metadataKey,
        metadataValue,
        metadataType: "string",
      };
    }

    // For a new condition, pick a sensible default comparator for the default
    // selected field to avoid transient MUI warnings when the Select mounts.
    const defaultSelectedFieldName = searchFields[0]?.name || "";
    const defaultSelectedFieldObj =
      searchFields.find((f) => f.name === defaultSelectedFieldName) ||
      searchFields[0];
    const availableComparatorsForDefault = comparatorForSearchField(
      defaultSelectedFieldObj,
      QueryTarget.Datasets,
      "string",
    );

    return {
      resourcePrefix: getDefaultPrefix(forEachPrimitive),
      selectedField: defaultSelectedFieldName,
      comparator: availableComparatorsForDefault[0] || ("" as Comparator),
      value: "",
      dateValue: todayAt8AMLocal.toDate(),
      metadataKey: "",
      metadataValue: "",
      metadataType: "string",
    };
  };

  const form = useForm<TriggerConditionInputForm>({
    defaultValues: getDefaultValues(),
    resolver: zodResolver(
      triggerConditionInputSchema,
    ) as Resolver<TriggerConditionInputForm>,
  });

  const onSubmit: SubmitHandler<TriggerConditionInputForm> = (data) => {
    let finalField = data.selectedField;
    let finalValue: string = data.value;

    // Find the selected field to get its type
    const selectedField = searchFields.find(
      (f) => f.name === data.selectedField,
    );

    // Handle metadata fields
    if (selectedField?.type === "metadata") {
      finalField = `metadata.${data.metadataKey}`;
      finalValue = data.metadataValue;
    } else if (selectedField?.type === "date" && data.dateValue) {
      finalValue = dayjs(data.dateValue).toISOString();
    }

    // Build field name with prefix
    const fieldWithPrefix = buildFieldName(finalField, data.resourcePrefix);

    const condition: TriggerCondition = {
      id: conditionToEdit?.id || crypto.randomUUID(),
      resourcePrefix: data.resourcePrefix,
      field: fieldWithPrefix,
      comparator: data.comparator,
      value: finalValue,
      searchFieldType: selectedField?.type || "other",
    };

    if (conditionToEdit) {
      onUpdate(condition);
    } else {
      onCreate(condition);
    }
  };

  return (
    <FormProvider {...form}>
      <form
        onSubmit={(event) => {
          void form.handleSubmit(onSubmit)(event);
        }}
      >
        <TriggerConditionInputFields
          searchFields={searchFields}
          forEachPrimitive={forEachPrimitive}
          availableMetadataKeys={availableMetadataKeys}
          onCancel={onCancel}
          conditionToEdit={conditionToEdit}
        />
      </form>
    </FormProvider>
  );
};
